import React from 'react';
import { Button, EmptyState } from '@meddihub/ui';
import useTranslation from '../../../Hooks/useTranslation';
import ContentSkeleton from '../../Base/Components/ContentSkeleton';
import { MzcrPatientState } from '../Services/useMzcrPatientService';
import { CloudOffIcon, IdCardIcon, KeyIcon, UserSearchIcon } from './icons';

interface Props {
	state: MzcrPatientState;
	/** Something is being loaded - the action buttons show it. */
	isBusy: boolean;
	onRetry: () => void;
	onLookupRid: () => void;
	/** Switches the patient card to the Profile tab (to fix the patient's name or insurance number). */
	onOpenProfile?: () => void;
	/** Opens the workplace settings on the MZČR tab – only for users who may manage the workplace. */
	onOpenCertificateSettings?: () => void;
}

/**
 * The MZČR tab of the patient card while the service itself cannot be shown - loading, no
 * certificate, an error or the RID of the patient not known yet.
 */
const MzcrPatientStateView: React.FC<Props> = ({
	state,
	isBusy,
	onRetry,
	onLookupRid,
	onOpenProfile,
	onOpenCertificateSettings,
}) => {
	const { t } = useTranslation();

	switch (state.status) {
		case 'loading':
			return <ContentSkeleton />;

		case 'noCertificate':
			return (
				<EmptyState
					tone='info'
					icon={<KeyIcon />}
					title={t('component.mzcr.patientState.noCertificateTitle')}
					actions={
						onOpenCertificateSettings && (
							<Button variant='success' onClick={onOpenCertificateSettings}>
								{t('component.mzcr.patientState.uploadCertificate')}
							</Button>
						)
					}>
					{onOpenCertificateSettings
						? t('component.mzcr.patientState.noCertificateManager')
						: t('component.mzcr.patientState.noCertificateMember')}
				</EmptyState>
			);

		case 'error':
			return (
				<EmptyState
					tone='danger'
					icon={<CloudOffIcon />}
					title={t('component.mzcr.connectionFailed.title')}
					actions={
						<Button variant='link' onClick={onRetry} isLoading={isBusy}>
							{t('component.mzcr.retry')}
						</Button>
					}>
					{t('component.mzcr.connectionFailed.text')}
				</EmptyState>
			);

		case 'ridMissingData':
			return (
				<EmptyState
					tone='danger'
					icon={<UserSearchIcon />}
					title={t('component.mzcr.patientState.missingDataTitle')}
					actions={
						onOpenProfile && (
							<Button variant='success' onClick={onOpenProfile}>
								{t('component.mzcr.patientState.fillInProfile')}
							</Button>
						)
					}>
					{t('component.mzcr.patientState.missingDataText')}
				</EmptyState>
			);

		case 'ridNotFound':
			return (
				<EmptyState
					tone='danger'
					icon={<UserSearchIcon />}
					title={t('component.mzcr.patientState.notFoundTitle')}
					actions={
						<>
							<Button
								variant='success'
								onClick={onLookupRid}
								isLoading={isBusy}>
								{t('component.mzcr.retry')}
							</Button>
							{onOpenProfile && (
								<Button variant='link' onClick={onOpenProfile}>
									{t('component.mzcr.patientState.openProfile')}
								</Button>
							)}
						</>
					}>
					{t('component.mzcr.patientState.notFoundText')}
				</EmptyState>
			);

		case 'ridNotPaired':
			return (
				<EmptyState
					tone='info'
					icon={<IdCardIcon />}
					title={t('component.mzcr.patientState.notPairedTitle')}
					actions={
						<Button variant='success' onClick={onLookupRid} isLoading={isBusy}>
							{t('component.mzcr.patientState.loadRid')}
						</Button>
					}>
					{t('component.mzcr.patientState.notPairedText')}
				</EmptyState>
			);

		default:
			return null;
	}
};

export default MzcrPatientStateView;
